import { Component, createRef } from 'react'
import _ from 'lodash'
import { Portal } from 'react-portal'
import { Button } from '@vzmi/shp_shared_compo'
import { idsOnlyParser } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import {
  formDecorator,
  Field
} from '../../../../Common/forms'
import {
  TextareaField
} from '../../../../Common/Form'
import {
  removeNotNumberAndComma
} from '../../../../../lib/parser'
import {
  delay
} from '../../../../../../lib/renderUtil'
import styles from './index.css'
import {
  getAttrMap,
  getIsAllowNewProduct,
  getTableRowsFromGroupedRows
} from '../helpers'
import { NewProductBtnWithPopup } from '../NewProductBtn'

export const PRODUCT_IDS_LIMIT = 20

export const FIELD = {
  PRODUCT_IDS: 'productIds',
  GLOBAL: 'global'
}

export const validateProductIds = (values) => {
  const productId = values[FIELD.PRODUCT_IDS]
  const productIds = idsOnlyParser(productId)
  if (_.size(productIds) > PRODUCT_IDS_LIMIT) {
    return { [FIELD.PRODUCT_IDS]: `最多輸入${PRODUCT_IDS_LIMIT}筆` }
  }

  return {}
}

export const validateFieldEmpty = (values) => {
  const listingId = values[FIELD.PRODUCT_IDS]

  // 不顯示 error message 但確保沒有輸入商品 disabled 匯入按鈕需要有錯誤訊息
  if (_.isEmpty(listingId)) {
    return { [FIELD.GLOBAL]: 'required' }
  }

  return {}
}

export const getCount = inputProps => {
  const inputValue = _.get(inputProps, 'input.value', '')
  const count = _.size(idsOnlyParser(inputValue))
  return count
}

const INIT_VALUES = {
  [FIELD.PRODUCT_IDS]: '',
  [FIELD.GLOBAL]: ''
}

const syncValidator = (values) => {
  const errors = _.merge(
    validateProductIds(values),
    validateFieldEmpty(values)
  )
  return errors
}

export const CONFIG = {
  form: 'UPDATE_LISTING_MODEL_IMPORT_FORM',
  validate: syncValidator,
  initialValues: INIT_VALUES
}

class ImportForm extends Component {
  constructor (props) {
    super(props)
    this.state = {
      formPosition: {
        position: 'absolute'
      },
      areaSize: {}
    }
    this.importFormArea = createRef()
    this.importForm = createRef()
    this.debounceOnChange = _.debounce(this.handleProductIdsChange, 300)
  }

  componentDidMount () {
    // 這個表單放在 proposal form 裡面，form 包含 form 是錯誤的格式
    // 因此用 Portal 將這個表單移置 document 底下
    // 但需要固定位置所以需要 listen resize
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = async () => {
    const {
      formPosition: defaultFormPosition
    } = this.state
    const { formPosition, areaSize } = this.calcFormPosition()

    this.setState({
      formPosition: {
        ...defaultFormPosition,
        ...formPosition
      },
      areaSize
    })

    // 送出提案失敗時畫面會移置頂部，過程中計算位置會導致位置錯誤，
    // 所以會重新確認直到位置一致才代表 resize 完成
    await delay(100)

    const { formPosition: currentFormPosition } = this.calcFormPosition()
    if (!_.isEqual(formPosition, currentFormPosition)) {
      this.handleResize()
    }
  }

  calcFormPosition = () => {
    const importFormAreaElement = this.importFormArea.current
    const importFormElement = this.importForm.current
    if (!importFormAreaElement || !importFormElement) {
      return {}
    }

    // 表單位置參考最外層 div
    const currentFormPosition = _.pick(
      importFormAreaElement.getBoundingClientRect(),
      ['top', 'left', 'width']
    )
    currentFormPosition.left += window.pageXOffset
    currentFormPosition.top += window.pageYOffset
    // 因為 position: 'absolute' 所以需要有個元素支撐原本表單大小
    const areaSize = _.pick(
      importFormElement.getBoundingClientRect(),
      ['height']
    )

    return {
      formPosition: currentFormPosition,
      areaSize
    }
  }

  onSubmit = e => {
    this.props.handleSubmit(e)
  }

  handleProductIdsChange = e => {
    const {
      isTwoLayerAttr,
      updateFormSyncErrors,
      clearAsyncError,
      dataMap,
      sortList
    } = this.props
    const attrMap = getAttrMap(isTwoLayerAttr)
    const productIds = _.concat(
      idsOnlyParser(_.get(e, 'target.value', '')),
      getTableRowsFromGroupedRows(dataMap, sortList, row => _.get(row, attrMap.productId))
    )
    const duplicateIds = _.flow(
      ids => _.countBy(ids, _.toString),
      countedMap => _.omitBy(countedMap, value => value === 1),
      _.keys
    )(productIds)
    if (_.isEmpty(duplicateIds)) {
      clearAsyncError(FIELD.PRODUCT_IDS)
      return
    }

    updateFormSyncErrors({ [FIELD.PRODUCT_IDS]: `重複商品編號 ${_.join(duplicateIds, ', ')}` })
  }

  render () {
    const {
      submitting,
      invalid,
      openNewProductModal,
      listing,
      isNoLayerAttr,
      isLayerAttrChanged
    } = this.props
    const {
      formPosition,
      areaSize
    } = this.state
    const isAllowNewProduct = getIsAllowNewProduct(listing)
    // update proposal 系列最外層都有包一個 form，但巢狀 form 是錯誤格式
    // 因此用 portal 將 form 移至 body 底下避免 warning
    return (
      <div ref={this.importFormArea}>
        <div style={areaSize} />
        <Portal>
          <form
            ref={this.importForm}
            style={{ position: 'absolute', ...formPosition }}
            date-role='import-form'
          >
            <Field
              name={FIELD.PRODUCT_IDS}
              data-role={FIELD.PRODUCT_IDS}
              countLimit={PRODUCT_IDS_LIMIT}
              customizeGetCount={getCount}
              component={TextareaField}
              className={styles.listingId}
              placeholder={`請輸入商品編號，以逗號或 ENTER 分隔，一次最多輸入 ${PRODUCT_IDS_LIMIT} 筆`}
              parse={removeNotNumberAndComma}
              onChange={this.debounceOnChange}
              disabled={isNoLayerAttr}
              keepErrorPosition
            />
            <span className={styles.separator} />
            <Button
              className={styles.importBtn}
              buttonType='outline'
              onClick={this.onSubmit}
              disabled={submitting || invalid || isNoLayerAttr}
            >
              匯入商品資料
            </Button>
            {
              // 非無屬性或無屬性變更成有屬性賣場才可以新增商品
              (isAllowNewProduct && (!isNoLayerAttr || isLayerAttrChanged)) && (
                <>
                  {' 或直接 '}
                  <NewProductBtnWithPopup
                    onClick={() => openNewProductModal()}
                  />
                </>
              )
            }
          </form>
        </Portal>
      </div>
    )
  }
}

export default formDecorator(CONFIG)(ImportForm)
